import Cookies from 'js-cookie'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

type AuthToken = string | null

type State = {
  token: AuthToken
  isAuthenticated: boolean
}

type Actions = {
  setToken: (_token: AuthToken) => void
  clearToken: () => void
  getAccessToken: () => string | null
}

export const STORAGE_NAME = 'sais-auth'
const COOKIE_EXPIRES_DAYS = 7
const COOKIE_OPTIONS = {
  expires: COOKIE_EXPIRES_DAYS,
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production'
}

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4)
  return atob(`${normalized}${padding}`)
}

const getTokenExp = (token: string) => {
  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

const isTokenExpired = (token: string) => {
  const exp = getTokenExp(token)
  return exp ? Date.now() >= exp * 1000 : false
}

const getCookieToken = () =>
  typeof window !== 'undefined'
    ? (() => {
        const token = Cookies.get(STORAGE_NAME) || null

        if (!token) {
          return null
        }

        if (isTokenExpired(token)) {
          Cookies.remove(STORAGE_NAME, { path: '/' })
          return null
        }

        return token
      })()
    : null

const initialToken = getCookieToken()

const initialState: State = {
  token: initialToken,
  isAuthenticated: !!initialToken
}

const useAuthStore = create(
  subscribeWithSelector(
    immer<State & Actions>((set, get) => ({
      ...initialState,
      setToken: (token) => {
        set((state) => {
          state.token = token
          state.isAuthenticated = !!token
        })
        if (token) {
          Cookies.set(STORAGE_NAME, token, COOKIE_OPTIONS)
        } else {
          Cookies.remove(STORAGE_NAME, { path: '/' })
        }
      },

      clearToken: () => {
        set((state) => {
          state.token = null
          state.isAuthenticated = false
        })
        Cookies.remove(STORAGE_NAME, { path: '/' })
      },
      getAccessToken: () => get().token
    }))
  )
)

export { useAuthStore }
